/**
 * Data Upload Hook
 * Manages server upload logic and processed file tracking
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RowDataChunkedUploader, { UploadProgress } from '../services/RowDataChunkedUploader';
import { DataUploadProgress, ColumnValidationErrors } from '../types/dataUpload.types';
import { sortByDate } from '../utils/dataUpload.utils';

export interface SelectedColumnsInput {
  hasSeparateDateAndTime: boolean;
  selectedColumn1Index: number;
  selectedColumn2Index: number;
  selectedColumn3Index: number;
}

export function buildSelectedColumns(input: SelectedColumnsInput): Record<string, string> {
  const result: Record<string, string> = {
    column1: input.selectedColumn1Index.toString(),
    column2: input.selectedColumn2Index.toString(),
  };
  if (input.hasSeparateDateAndTime) {
    result.column3 = input.selectedColumn3Index.toString();
  }
  return result;
}

interface UseDataUploadProps {
  isOnline: boolean;
  selectedFiles: File[];
  delimiter: string;
  radioHasHeader: string;
  selectedTimezone: string;
  hasSeparateDateAndTime: boolean;
  valueColumnName: string;
  selectedColumn1: string;
  selectedColumn1Index: number;
  selectedColumn2: string;
  selectedColumn2Index: number;
  selectedColumn3: string;
  selectedColumn3Index: number;
  customDateFormat: string;
  canUpload: () => Promise<{ allowed: boolean; message?: string }>;
  checkStorage: (sizeMB: number) => Promise<{ allowed: boolean; message?: string }>;
  refreshUsage: () => Promise<void>;
  onError: (
    payload: string | { message: string; code?: string; suggestion?: string },
    persistent?: boolean,
  ) => void;
  onSuccess: (message: string) => void;
  onValidationError: (errors: ColumnValidationErrors) => void;
  onUpgradeRequired: (message: string, resourceType: 'upload' | 'processing' | 'storage') => void;
  onStartProcessing: () => void;
  onStopProcessing: () => void;
  setProcessedTableData: (data: string[][]) => void;
}

export const useDataUpload = (props: UseDataUploadProps) => {
  const { t } = useTranslation('dataUpload');
  const {
    isOnline,
    selectedFiles,
    delimiter,
    radioHasHeader,
    selectedTimezone,
    hasSeparateDateAndTime,
    valueColumnName,
    selectedColumn1Index,
    selectedColumn2Index,
    selectedColumn3,
    selectedColumn3Index,
    customDateFormat,
    canUpload,
    checkStorage,
    refreshUsage,
    onError,
    onSuccess,
    onValidationError,
    onUpgradeRequired,
    onStartProcessing,
    onStopProcessing,
    setProcessedTableData
  } = props;

  const [processedFileIds, setProcessedFileIds] = useState<string[]>([]);
  const [originalFileNames, setOriginalFileNames] = useState<string[]>([]);
  const [standardProgress, setStandardProgress] = useState<DataUploadProgress | null>(null);

  /**
   * Handles validation errors and highlights problematic dropdowns
   */
  const handleValidationError = useCallback((errorMessage: string) => {
    const errors: ColumnValidationErrors = { column1: false, column2: false, column3: false };
    
    if (errorMessage.includes('Unsupported date format') || errorMessage.includes('Date parsing error')) {
      errors.column1 = true;
      if (hasSeparateDateAndTime) {
        errors.column2 = true;
      }
    }
    
    if (errorMessage.includes('value column') || errorMessage.includes('Value column')) {
      if (hasSeparateDateAndTime) {
        errors.column3 = true;
      } else {
        errors.column2 = true;
      }
    }
    
    onValidationError(errors);
    onError(errorMessage);
  }, [hasSeparateDateAndTime, onValidationError, onError]);

  /**
   * Uploads files to the server
   */
  const uploadFiles = useCallback(async (): Promise<boolean> => {
    if (!isOnline) {
      onError(t('errorMessages.noInternet'));
      return false;
    }

    // Quota checks
    const uploadCheck = await canUpload();
    if (!uploadCheck.allowed) {
      onUpgradeRequired(uploadCheck.message || 'Upload limit reached', 'upload');
      return false;
    }

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const totalSizeMB = totalSize / (1024 * 1024);
    const storageCheck = await checkStorage(totalSizeMB);
    if (!storageCheck.allowed) {
      onUpgradeRequired(storageCheck.message || 'Storage limit reached', 'storage');
      return false;
    }

    // Validation checks
    if (!selectedFiles.length) {
      onError(t('errorMessages.noFile'));
      return false;
    }

    if (!delimiter) {
      onError(t('errorMessages.selectDelimiter'));
      return false;
    }

    if (hasSeparateDateAndTime && !selectedColumn3) {
      onError(t('errorMessages.selectValueColumn'));
      return false;
    }

    onStartProcessing();

    let accumulatedData: string[][] = [];
    const fileNames: string[] = [];
    const fileIds: string[] = [];

    try {
      for (const file of selectedFiles) {
        try {
          const uploadId = await RowDataChunkedUploader.initializeUpload();
          
          const additionalData = {
            timezone: selectedTimezone,
            dropdown_count: hasSeparateDateAndTime ? '3' : '2',
            valueColumnName: valueColumnName,
            selected_columns: buildSelectedColumns({
              hasSeparateDateAndTime,
              selectedColumn1Index,
              selectedColumn2Index,
              selectedColumn3Index,
            }),
            custom_date_format: customDateFormat || undefined
          };
          
          const result = await RowDataChunkedUploader.uploadFile(
            file,
            uploadId,
            delimiter,
            radioHasHeader === "ja",
            (progress: UploadProgress) => {
              setStandardProgress({
                fileName: progress.fileName,
                uploadProgress: progress.uploadProgress,
                processingProgress: progress.processingProgress,
                status: progress.status,
                error: progress.error,
                message: progress.message,
                step: progress.step,
                eta: progress.eta,
                etaFormatted: progress.etaFormatted,
                currentStep: progress.currentStep,
                totalSteps: progress.totalSteps,
                currentFile: progress.currentFile,
                totalFiles: progress.totalFiles
              });

              if (progress.status === 'error') {
                const errorMsg = progress.error || 'Unknown error';
                handleValidationError(errorMsg);
              }
            },
            additionalData as Record<string, unknown>,
            async () => {
              await new Promise(resolve => setTimeout(resolve, 200));
              for (let i = 0; i < 3; i++) {
                await refreshUsage();
                if (i < 2) {
                  await new Promise(resolve => setTimeout(resolve, 300));
                }
              }
            }
          );
          
          fileNames.push(file.name);

          if (result && result.fileId) {
            fileIds.push(result.fileId as string);
          }

          const previewData = result?.preview || result?.data;
          if (previewData && Array.isArray(previewData)) {
            const currentFileData = previewData as string[][];
            const currentDataSorted = sortByDate(currentFileData);
            
            if (accumulatedData.length === 0) {
              accumulatedData = currentDataSorted;
            } else {
              accumulatedData = accumulatedData.concat(currentDataSorted.slice(1));
            }
          }
        } catch (error) {
          if (!navigator.onLine) {
            onError(t('errorMessages.noInternet'));
          } else if (error instanceof Error) {
            const typedErr = error as Error & { code?: string; suggestion?: string };
            onError(
              {
                message: typedErr.message,
                code: typedErr.code,
                suggestion: typedErr.suggestion,
              },
              /* persistent= */ true,
            );
          } else {
            onError(t('errorMessages.unexpectedError'), true);
          }
          onStopProcessing();
          return false;
        }
      }

      // Refresh usage after all uploads
      try {
        await refreshUsage();
      } catch (refreshError) {
        console.error('Failed to refresh usage data:', refreshError);
      }

      if (accumulatedData.length > 0) {
        const sortedData = sortByDate(accumulatedData);
        setProcessedTableData(sortedData);
      }

      setProcessedFileIds(fileIds);
      setOriginalFileNames(fileNames);
      onSuccess(t('successMessages.allFilesProcessed'));
      onStopProcessing();
      return true;

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : t('errorMessages.unknownError');
      onError(t('errorMessages.serverError') + ': ' + errorMsg, true);
      onStopProcessing();
      return false;
    }
  }, [
    isOnline, selectedFiles, delimiter, radioHasHeader, selectedTimezone,
    hasSeparateDateAndTime, valueColumnName, selectedColumn1Index,
    selectedColumn2Index, selectedColumn3, selectedColumn3Index,
    customDateFormat, canUpload, checkStorage, refreshUsage, onError, onSuccess,
    onUpgradeRequired, onStartProcessing, onStopProcessing, setProcessedTableData,
    handleValidationError, t
  ]);

  /**
   * Resets upload state
   */
  const resetUpload = useCallback(() => {
    setProcessedFileIds([]);
    setOriginalFileNames([]);
    setStandardProgress(null);
  }, []);

  return {
    processedFileIds,
    setProcessedFileIds,
    originalFileNames,
    standardProgress,
    uploadFiles,
    resetUpload
  };
};
